import {
	DsaiIngestionPipelineStage,
	DsaiIngestionProgressStatus,
	DsaiIngestionStageState,
	DsaiReaderState,
	type IngestionOutcomeCountsRes,
	type IngestionProgressRes,
	type IngestionStageRes,
	type SecondsRangeRes,
} from '@dealstack/shared/lib/generated';
import type { TranslationRef } from '../../AnalysisWorkspace/components/primitives/primitives.types';
import { formatCount, formatLibraryDate, formatRate } from '../ingestion.utils';
import type {
	ProcessingCardContent,
	ProcessingFactView,
	ProcessingReaderStatusView,
	ProcessingStageView,
	ProcessingStepKey,
	ProcessingSubheading,
	ProcessingSubstepView,
} from '../ProcessingCard/ProcessingCard.types';
import type { ProcessingCardInput, StageOutcomeKey } from './useBatchProcessing.types';

const STAGE_KEYS: Record<ProcessingStepKey, { title: string; description: string }> = {
	warmUp: {
		title: 'dealstack_ai.ingest.stage.warm_up.title',
		description: 'dealstack_ai.ingest.stage.warm_up.description',
	},
	intake: {
		title: 'dealstack_ai.ingest.stage.intake.title',
		description: 'dealstack_ai.ingest.stage.intake.description',
	},
	reading: {
		title: 'dealstack_ai.ingest.stage.reading.title',
		description: 'dealstack_ai.ingest.stage.reading.description',
	},
	extracting: {
		title: 'dealstack_ai.ingest.stage.extracting.title',
		description: 'dealstack_ai.ingest.stage.extracting.description',
	},
	resolving: {
		title: 'dealstack_ai.ingest.stage.resolving.title',
		description: 'dealstack_ai.ingest.stage.resolving.description',
	},
	importing: {
		title: 'dealstack_ai.ingest.stage.importing.title',
		description: 'dealstack_ai.ingest.stage.importing.description',
	},
};

/**
 * Each document outcome, under the step whose end makes it final: too many pages and unsupported are settled before a
 * document is read, the rest by its extraction. Duplicates are in intake's own count.
 */
const STAGE_OUTCOMES: Partial<Record<DsaiIngestionPipelineStage, StageOutcomeKey[]>> = {
	reading: ['tooManyPages', 'unsupported'],
	extracting: ['extracted', 'partial', 'couldntRead'],
};

const OUTCOME_KEYS: Record<StageOutcomeKey, string> = {
	extracted: 'dealstack_ai.ingest.outcome.extracted',
	partial: 'dealstack_ai.ingest.outcome.partial',
	couldntRead: 'dealstack_ai.ingest.outcome.couldnt_read',
	tooManyPages: 'dealstack_ai.ingest.outcome.too_many_pages',
	unsupported: 'dealstack_ai.ingest.outcome.unsupported',
};

const HEADING_KEYS: Record<DsaiIngestionProgressStatus, string> = {
	processing: 'dealstack_ai.ingest.processing.heading.processing',
	complete: 'dealstack_ai.ingest.processing.heading.complete',
	cancelled: 'dealstack_ai.ingest.processing.heading.cancelled',
	error: 'dealstack_ai.ingest.processing.heading.error',
};

const READER_STATUS: Record<DsaiReaderState, ProcessingReaderStatusView> = {
	starting: {
		messageKey: 'dealstack_ai.ingest.processing.reader.starting',
		hintKey: 'dealstack_ai.ingest.processing.reader.starting_hint',
	},
	ready: {
		messageKey: 'dealstack_ai.ingest.processing.reader.ready',
		hintKey: 'dealstack_ai.ingest.processing.reader.ready_hint',
	},
};

const toSubsteps = (
	key: DsaiIngestionPipelineStage,
	state: DsaiIngestionStageState,
	outcomes: IngestionOutcomeCountsRes
): ProcessingSubstepView[] =>
	(STAGE_OUTCOMES[key] ?? [])
		.filter((outcome) => outcomes[outcome] > 0)
		.map((outcome) => ({
			key: outcome,
			label: { key: OUTCOME_KEYS[outcome], values: { count: outcomes[outcome] } },
			state,
		}));

const doneOfTotal = (key: string, done: number, total: number): TranslationRef => ({
	key,
	values: { done: formatCount(done), total },
});

/** Files and duplicates at intake, pages while reading, documents while extracting; the whole-batch steps have none. */
const stageCount = (
	key: DsaiIngestionPipelineStage,
	progress: IngestionProgressRes
): TranslationRef | null => {
	switch (key) {
		case DsaiIngestionPipelineStage.INTAKE: {
			const duplicates = progress.outcomes.duplicatesRemoved;
			return duplicates > 0
				? {
						key: 'dealstack_ai.ingest.processing.count.files_and_duplicates',
						values: { files: progress.filesTotal, duplicates },
					}
				: {
						key: 'dealstack_ai.ingest.processing.count.files',
						values: { count: progress.filesTotal },
					};
		}
		case DsaiIngestionPipelineStage.READING:
			return doneOfTotal(
				'dealstack_ai.ingest.processing.count.pages',
				progress.pagesRead,
				progress.pagesTotal
			);
		case DsaiIngestionPipelineStage.EXTRACTING:
			return doneOfTotal(
				'dealstack_ai.ingest.processing.count.documents_extracted',
				progress.documentsExtracted,
				progress.documentsTotal
			);
		default:
			return null;
	}
};

/** The server's observed rate for the running step: pages a second while reading, documents a minute while extracting. */
const stageRate = (
	key: DsaiIngestionPipelineStage,
	progress: IngestionProgressRes
): TranslationRef | null => {
	if (key === DsaiIngestionPipelineStage.READING && progress.pagesPerSecond !== null) {
		return {
			key: 'dealstack_ai.ingest.processing.rate.pages_per_second',
			values: { rate: formatRate(progress.pagesPerSecond) },
		};
	}
	if (key === DsaiIngestionPipelineStage.EXTRACTING && progress.documentsPerMinute !== null) {
		return {
			key: 'dealstack_ai.ingest.processing.rate.documents_per_minute',
			values: { rate: formatRate(progress.documentsPerMinute) },
		};
	}
	return null;
};

const ratio = (done: number, total: number): number => (total > 0 ? Math.min(1, done / total) : 0);

/** 0..1 for the steps the progress measures: pages read, documents through extraction; null for the rest. */
const stageFraction = (
	key: DsaiIngestionPipelineStage,
	progress: IngestionProgressRes
): number | null => {
	if (key === DsaiIngestionPipelineStage.READING) {
		return ratio(progress.pagesRead, progress.pagesTotal);
	}
	if (key === DsaiIngestionPipelineStage.EXTRACTING) {
		return ratio(progress.documentsExtracted, progress.documentsTotal);
	}
	return null;
};

/** Reading waits behind the warm-up: it shows as pending until the warm-up step is done. */
const displayedState = (
	stage: IngestionStageRes,
	warmUp: DsaiIngestionStageState | null
): DsaiIngestionStageState =>
	stage.key === DsaiIngestionPipelineStage.READING &&
	stage.state === DsaiIngestionStageState.RUNNING &&
	warmUp === DsaiIngestionStageState.RUNNING
		? DsaiIngestionStageState.PENDING
		: stage.state;

const toStageView = (
	stage: IngestionStageRes,
	progress: IngestionProgressRes,
	failedStage: DsaiIngestionPipelineStage | null
): ProcessingStageView => {
	const state = displayedState(stage, progress.readerWarmUp);
	const counted = state !== DsaiIngestionStageState.PENDING || stage.key === failedStage;
	const running = state === DsaiIngestionStageState.RUNNING && stage.key !== failedStage;
	return {
		key: stage.key,
		titleKey: STAGE_KEYS[stage.key].title,
		descriptionKey: STAGE_KEYS[stage.key].description,
		state,
		count: counted ? stageCount(stage.key, progress) : null,
		progress: running ? stageFraction(stage.key, progress) : null,
		rate: running ? stageRate(stage.key, progress) : null,
		substeps: toSubsteps(stage.key, state, progress.outcomes),
	};
};

/** "1:05": minutes and seconds since the batch started. */
const formatElapsed = (seconds: number): string => {
	const whole = Math.max(0, Math.floor(seconds));
	return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/** While it runs, the warm-up counts up from the start, so a cold start never reads as a stalled card. */
const warmUpView = (
	state: DsaiIngestionStageState,
	startedAt: string,
	now: number
): ProcessingStageView => ({
	key: 'warmUp',
	titleKey: STAGE_KEYS.warmUp.title,
	descriptionKey: STAGE_KEYS.warmUp.description,
	state,
	count:
		state === DsaiIngestionStageState.RUNNING
			? {
					key: 'dealstack_ai.ingest.processing.count.warm_up_elapsed',
					values: { elapsed: formatElapsed((now - Date.parse(startedAt)) / 1000) },
				}
			: null,
	progress: null,
	rate: null,
	substeps: [],
});

/** The served stages, with the GPU warm-up step before reading when the batch waited on a reader starting from zero. */
const toStageViews = (
	progress: IngestionProgressRes,
	failedStage: DsaiIngestionPipelineStage | null,
	now: number
): ProcessingStageView[] =>
	progress.stages.flatMap((stage) => {
		const view = toStageView(stage, progress, failedStage);
		return stage.key === DsaiIngestionPipelineStage.READING && progress.readerWarmUp !== null
			? [warmUpView(progress.readerWarmUp, progress.startedAt, now), view]
			: [view];
	});

/** 0..1 over the stages: each done stage counts whole, the running one by its own progress. */
const progressFraction = (progress: IngestionProgressRes | undefined): number => {
	if (progress === undefined || progress.stages.length === 0) {
		return 0;
	}
	if (progress.status === DsaiIngestionProgressStatus.COMPLETE) {
		return 1;
	}
	const reached = progress.stages.reduce((total, stage) => {
		if (stage.state === DsaiIngestionStageState.DONE) {
			return total + 1;
		}
		if (stage.state === DsaiIngestionStageState.RUNNING) {
			return total + (stageFraction(stage.key, progress) ?? 0);
		}
		return total;
	}, 0);
	return reached / progress.stages.length;
};

const toMinutes = (seconds: number): number => Math.max(1, Math.ceil(seconds / 60));

/** "4 min", or "4–9 min" when the range spans minutes. */
export const etaCopy = ({ lowSeconds, highSeconds }: SecondsRangeRes): TranslationRef => {
	const low = toMinutes(lowSeconds);
	const high = toMinutes(highSeconds);
	if (low >= high) {
		return { key: 'dealstack_ai.ingest.processing.eta.minutes', values: { minutes: low } };
	}
	return { key: 'dealstack_ai.ingest.processing.eta.range', values: { low, high } };
};

const processingSubheading = (progress: IngestionProgressRes): ProcessingSubheading => {
	if (progress.firstBatchEta === null) {
		return { lead: { key: 'dealstack_ai.ingest.processing.eta.estimating' }, emphasis: null };
	}
	return {
		lead: {
			key:
				progress.backBookId === null
					? 'dealstack_ai.ingest.processing.eta.label'
					: 'dealstack_ai.ingest.processing.eta.first_batch_label',
		},
		emphasis: etaCopy(progress.firstBatchEta),
	};
};

const retentionSubheading = (
	filesDeleteOn: string | null,
	filesDeleted: boolean
): ProcessingSubheading | null => {
	if (filesDeleted) {
		return { lead: { key: 'dealstack_ai.ingest.processing.files_deleted' }, emphasis: null };
	}
	if (filesDeleteOn === null) {
		return null;
	}
	return {
		lead: {
			key: 'dealstack_ai.ingest.processing.retention',
			values: { date: formatLibraryDate(filesDeleteOn) },
		},
		emphasis: null,
	};
};

const subheadingOf = ({
	progress,
	filesDeleteOn,
	filesDeleted,
}: ProcessingCardInput): ProcessingSubheading | null => {
	if (progress === undefined) {
		return null;
	}
	switch (progress.status) {
		case DsaiIngestionProgressStatus.PROCESSING:
			return processingSubheading(progress);
		case DsaiIngestionProgressStatus.COMPLETE:
			return {
				lead: { key: 'dealstack_ai.ingest.processing.complete_lead' },
				emphasis: { key: 'dealstack_ai.ingest.processing.complete_emphasis' },
			};
		case DsaiIngestionProgressStatus.CANCELLED:
			return retentionSubheading(filesDeleteOn, filesDeleted);
		case DsaiIngestionProgressStatus.ERROR:
			return { lead: { key: 'dealstack_ai.ingest.processing.error_lead' }, emphasis: null };
	}
};

const ofTotal = (done: number, total: number): TranslationRef => ({
	key: 'dealstack_ai.ingest.processing.facts.of_total',
	values: { done: formatCount(done), total: formatCount(total) },
});

/** Documents through extraction and pages read of their totals, and the back book's batch count. */
const toFacts = (progress: IngestionProgressRes): ProcessingFactView[] => [
	{
		key: 'documents',
		labelKey: 'dealstack_ai.ingest.processing.facts.documents_extracted',
		value: ofTotal(progress.documentsExtracted, progress.documentsTotal),
	},
	{
		key: 'pages',
		labelKey: 'dealstack_ai.ingest.processing.facts.pages_read',
		value: ofTotal(progress.pagesRead, progress.pagesTotal),
	},
	...(progress.backBookId === null
		? []
		: [
				{
					key: 'batches',
					labelKey: 'dealstack_ai.ingest.processing.facts.split_into',
					value: {
						key: 'dealstack_ai.ingest.processing.facts.batch_count',
						values: { count: progress.batchCount },
					},
				},
			]),
];

/** The reader line under the checklist, unless the warm-up step already says it. */
const readerStatusOf = (
	progress: IngestionProgressRes | undefined
): ProcessingReaderStatusView | null => {
	if (progress === undefined || progress.readerState === null || progress.readerWarmUp !== null) {
		return null;
	}
	return READER_STATUS[progress.readerState];
};

export const toProcessingCardContent = (input: ProcessingCardInput): ProcessingCardContent => {
	const { progress, isStalled, now } = input;
	const phase = progress?.status ?? DsaiIngestionProgressStatus.PROCESSING;
	const failedStage = phase === 'error' ? progress?.stage ?? null : null;
	return {
		phase,
		failedStage,
		headingKey: HEADING_KEYS[phase],
		subheading: subheadingOf(input),
		facts: progress === undefined ? [] : toFacts(progress),
		stages: progress === undefined ? [] : toStageViews(progress, failedStage, now),
		progress: progressFraction(progress),
		isLoading: progress === undefined,
		readerStatus: readerStatusOf(progress),
		noticeKey: isStalled ? 'dealstack_ai.ingest.processing.stalled' : null,
	};
};
